import type { ValueSet } from '@medplum/fhirtypes';
import type { MedplumClient } from '@medplum/core';
import { CMS_PFS_PROCEDURE_CODES_VALUE_SET } from '@unify/fhir-model/constants';

export interface CmsPfsProcedureCode {
  code: string;
  display: string;
  system?: string;
}

export const CMS_PFS_DROPDOWN_LIMIT = 20;

/**
 * Medplum's `ValueSet/$expand` filter matches concept display text only, so a
 * search for "90834" returns nothing. The whole compose is read once instead
 * (~190 KB gzipped) and filtered locally, which also matches on code.
 */
let cachedConcepts: Promise<CmsPfsProcedureCode[]> | undefined;

export function flattenValueSetConcepts(valueSet: ValueSet | undefined): CmsPfsProcedureCode[] {
  return (valueSet?.compose?.include ?? []).flatMap((include) =>
    (include.concept ?? []).flatMap((concept) => {
      const code = concept.code?.trim().toUpperCase();
      if (!code) return [];
      return [{
        code,
        display: concept.display?.trim() || code,
        system: include.system,
      }];
    }));
}

export function filterCmsPfsProcedureCodes(
  concepts: CmsPfsProcedureCode[],
  query: string,
  limit = CMS_PFS_DROPDOWN_LIMIT,
): CmsPfsProcedureCode[] {
  const needle = query.trim().toLowerCase();
  const codeMatches: CmsPfsProcedureCode[] = [];
  const displayMatches: CmsPfsProcedureCode[] = [];

  for (const concept of concepts) {
    if (!needle || concept.code.toLowerCase().startsWith(needle)) {
      codeMatches.push(concept);
      if (codeMatches.length >= limit) break;
    } else if (
      displayMatches.length < limit
      && concept.display.toLowerCase().includes(needle)
    ) {
      displayMatches.push(concept);
    }
  }

  return [...codeMatches, ...displayMatches].slice(0, limit);
}

export function cmsPfsOptionLabel(concept: CmsPfsProcedureCode): string {
  return `${concept.code} – ${concept.display}`;
}

export async function loadCmsPfsProcedureCodes(
  medplum: MedplumClient,
): Promise<CmsPfsProcedureCode[]> {
  if (!cachedConcepts) {
    cachedConcepts = medplum
      .searchOne('ValueSet', { url: CMS_PFS_PROCEDURE_CODES_VALUE_SET })
      .then((valueSet) => {
        const concepts = flattenValueSetConcepts(valueSet);
        if (concepts.length === 0) {
          throw new Error(`Empty or missing ValueSet ${CMS_PFS_PROCEDURE_CODES_VALUE_SET}`);
        }
        return concepts;
      })
      .catch((err: unknown) => {
        cachedConcepts = undefined;
        throw err;
      });
  }
  return cachedConcepts;
}
